'use client';

import type { ReactNode } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@kit/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@kit/ui/input-group';
import { Trans } from '@kit/ui/trans';

const EmailOnlySchema = z.object({
  email: z.string().email(),
});

export type EmailOnlyValues = z.infer<typeof EmailOnlySchema>;

export function EmailIdentifyForm({
  onSubmit,
  loading = false,
  defaultEmail = '',
  captchaField,
}: {
  onSubmit: (values: EmailOnlyValues) => unknown;
  loading?: boolean;
  defaultEmail?: string;
  captchaField?: ReactNode;
}) {
  const { t } = useTranslation('auth');

  const form = useForm({
    resolver: zodResolver(EmailOnlySchema),
    defaultValues: { email: defaultEmail },
  });

  return (
    <Form {...form}>
      <form
        className="flex w-full flex-col gap-y-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputGroup className="dark:bg-background">
                  <InputGroupAddon>
                    <Mail className="h-4 w-4" />
                  </InputGroupAddon>

                  <InputGroupInput
                    data-test="email-input"
                    required
                    type="email"
                    autoFocus
                    placeholder={t('emailPlaceholder')}
                    {...field}
                  />
                </InputGroup>
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        {captchaField && <div className="flex justify-center">{captchaField}</div>}

        <Button
          data-test="auth-continue-button"
          className="group w-full"
          type="submit"
          disabled={loading}
        >
          <If condition={loading}>
            <Trans i18nKey="auth:checkingAccount" />
          </If>

          <If condition={!loading}>
            <span className="flex items-center">
              <Trans i18nKey="common:continue" />
              <ArrowRight className="zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4 delay-500 duration-500" />
            </span>
          </If>
        </Button>
      </form>
    </Form>
  );
}
